import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { THEME_PREFERENCES_KEY } from '@app/shared/constants/settings.constants';

export type ThemePreference = 'system' | 'dark' | 'neutral' | 'light';
export type ResolvedTheme = 'dark' | 'neutral' | 'light';

export interface ThemeUserScope {
  accountId: string | number;
  userId: string | number;
  loginType: string | number;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly systemDarkQuery = '(prefers-color-scheme: dark)';
  private readonly fallbackPreference: ThemePreference = 'system';
  private userScopeKey: string | null = null;
  private mediaQueryList: MediaQueryList | null = null;

  private readonly themePreferenceSubject = new BehaviorSubject<ThemePreference>(this.fallbackPreference);
  private readonly resolvedThemeSubject = new BehaviorSubject<ResolvedTheme>('light');

  themePreference$ = this.themePreferenceSubject.asObservable();
  resolvedTheme$ = this.resolvedThemeSubject.asObservable();

  constructor() {
    this.bindSystemThemeListener();
  }

  initialize(): void {
    this.applyPreference(this.getPreference());
  }

  setUserScope(scope: ThemeUserScope | null): void {
    this.userScopeKey = scope ? this.buildScopeKey(scope) : null;
    this.applyPreference(this.getPreference());
  }

  setPreference(preference: ThemePreference): void {
    const normalizedPreference = this.normalizePreference(preference);
    if (this.userScopeKey) {
      const preferences = this.readStoredPreferences();
      preferences[this.userScopeKey] = normalizedPreference;
      localStorage.setItem(THEME_PREFERENCES_KEY, JSON.stringify(preferences));
    }

    this.applyPreference(normalizedPreference);
  }

  getPreference(): ThemePreference {
    if (!this.userScopeKey) {
      return this.fallbackPreference;
    }

    const preferences = this.readStoredPreferences();
    return this.normalizePreference(preferences[this.userScopeKey]);
  }

  getResolvedTheme(): ResolvedTheme {
    return this.resolvedThemeSubject.value;
  }

  private applyPreference(preference: ThemePreference): void {
    const resolvedTheme = this.resolveTheme(preference);
    this.themePreferenceSubject.next(preference);
    this.resolvedThemeSubject.next(resolvedTheme);

    document.documentElement.setAttribute('data-mmp-theme-preference', preference);
    document.documentElement.setAttribute('data-mmp-theme', resolvedTheme);
  }

  private resolveTheme(preference: ThemePreference): ResolvedTheme {
    if (preference === 'dark' || preference === 'neutral' || preference === 'light') {
      return preference;
    }

    return this.prefersDarkMode() ? 'dark' : 'light';
  }

  private prefersDarkMode(): boolean {
    if (this.mediaQueryList) {
      return this.mediaQueryList.matches;
    }

    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia(this.systemDarkQuery).matches;
  }

  private bindSystemThemeListener(): void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    this.mediaQueryList = window.matchMedia(this.systemDarkQuery);
    const listener = () => {
      if (this.themePreferenceSubject.value === 'system') {
        this.applyPreference('system');
      }
    };

    if (this.mediaQueryList.addEventListener) {
      this.mediaQueryList.addEventListener('change', listener);
      return;
    }

    if (this.mediaQueryList.addListener) {
      this.mediaQueryList.addListener(listener);
    }
  }

  private readStoredPreferences(): Record<string, ThemePreference> {
    try {
      const value = localStorage.getItem(THEME_PREFERENCES_KEY);
      if (!value) {
        return {};
      }

      const parsed = JSON.parse(value);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return {};
      }

      return parsed;
    } catch (_) {
      return {};
    }
  }

  private normalizePreference(preference: any): ThemePreference {
    if (preference === 'light' || preference === 'dark' || preference === 'neutral' || preference === 'system') {
      return preference;
    }

    return this.fallbackPreference;
  }

  private buildScopeKey(scope: ThemeUserScope): string {
    return `account:${scope.accountId}:user:${scope.userId}:type:${scope.loginType}`;
  }
}
